import React, { ReactNode } from "react";
import { Table } from "@tanstack/react-table";
import { DatePickerWithRange } from "@/components/custom/date-picker-with-range";
import { presets } from "@/constants/date-preset";
import { Input } from "@/components/ui/input";
import { DateRange } from "react-day-picker";
import { DataTableFacetedFilterCustom } from "@/components/data-table/data-table-faceted-filter-custom";
import { taxiStatusItems } from "@/constants/status";

interface OrderTableFiltersProps {
  table: Table<any>;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  selectedStatus: string;
  statusSummary: Map<string, number>;
  statusFilter: string[];
  vehicles?: any[];
  selectedVehicles?: string[];
  clientFilter?: string;
  pickupFilter?: string;
  drivers?: any[];
  selectedDrivers?: string[];
  sources?: string[];

  onStatusFilterChange: (selected: string[]) => void;
  onVehicleFilterChange: (selected: string[]) => void;
  onDriverFilterChange: (selected: string[]) => void;
  onClientFilterChange: (selected: string) => void;
  onPickupFilterChange: (selected: string) => void;
  onSourceChange: (
    selected: string[]
  ) => void;
  secondFilter?: ReactNode;
  assignByDriver?: boolean
}

export const  OrderTableFilters: React.FC<OrderTableFiltersProps> = ({
  table,
  dateRange,
  setDateRange,
  selectedStatus,
  statusSummary,
  statusFilter,
  vehicles,
  selectedVehicles,
  drivers,
  selectedDrivers,
  sources,
  clientFilter,
  pickupFilter,
  onStatusFilterChange,
  onVehicleFilterChange,
  onDriverFilterChange,
  onClientFilterChange,
  onPickupFilterChange,
  onSourceChange,
  assignByDriver
}) => {
  return (
    <>
      <DatePickerWithRange
        title="Filter by pickup time"
        date={dateRange}
        presets={presets}
        setDate={(range) => {
          if (range) {
            setDateRange(range);
          }
        }}
      />

      <Input
        placeholder="Client details..."
        value={clientFilter}
        onChange={(event) => onClientFilterChange(event.target.value)}
        className="h-8 w-[200px] lg:w-[250px]"
      />

      <Input
        placeholder="Filter pickup..."
        value={pickupFilter}
        onChange={(event) => onPickupFilterChange(event.target.value)}
        className="h-8 w-[200px] lg:w-[250px]"
      />

      {table?.getColumn("status") && selectedStatus === "ALL" && (
        <DataTableFacetedFilterCustom
          column={table.getColumn("status")}
          title="Status"
          defaultFacets={statusSummary}
          selectedValues={
            statusFilter ? new Set(statusFilter) : new Set<string>()
          }
          onChange={onStatusFilterChange}
          options={taxiStatusItems.map((item) => ({
            label: item.label,
            value: item.status,
          }))}
        />
      )}

      {table?.getColumn("vehicle") && vehicles && (
        <DataTableFacetedFilterCustom
          column={table.getColumn("vehicle")}
          title="Vehicle"
          selectedValues={
            selectedVehicles && selectedVehicles.length > 0
              ? new Set(selectedVehicles)
              : new Set<string>()
          }
          onChange={onVehicleFilterChange}
          options={vehicles.map((item) => ({
            label: item.nickname ?? item.license_plate,
            value: item.id,
          }))}
        />
      )}

      {assignByDriver && drivers && (
        <DataTableFacetedFilterCustom
          title="Driver"
          selectedValues={
            selectedDrivers && selectedDrivers.length > 0
              ? new Set(selectedDrivers)
              : new Set<string>()
          }
          onChange={onDriverFilterChange}
          options={drivers.flatMap((item) =>
            item.drivers.map((driver) => ({
              label: driver.name,
              value: driver.id,
            })),
          )}
        />
      )}

      {table.getColumn("source") && sources && (
        <DataTableFacetedFilterCustom
          column={table.getColumn("source")}
          title="Source"
          selectedValues={sources && sources.length > 0 ? new Set(sources) : new Set<string>()} 
          onChange={onSourceChange}
          options={[
            { label: "Dispatch", value: "DISPATCH" },
            { label: "Client app", value: "CLIENT_APP" },
            { label: "Admin app", value: "ADMIN_APP" },
            { label: "Taximeter", value: "TAXIMETER" },
          ]}
        />
      )}
    </>
  );
};
